import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Typography,
  Checkbox,
  Divider,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { VendorStatus } from "./VendorStatus";
import { ContactInfo } from "./ContactInfo";
import { AdditionalInfo } from "./AdditionalInfo";

interface Customer {
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
  phone?: string;
  optInEmail?: boolean;
  smsOptIn?: boolean;
  birthDate?: string;
  birthdayUpdated?: boolean;
  hasAcceptedTerms?: boolean;
  termsAcceptedDate?: string;
  giftCards?: any[];
  giftCardsList?: any[];
  userDeliveryAddressList?: any[];
  privacySettings?: {
    gpcSignal: boolean;
  };
  vendorResultsList?: any[];
  punchhId?: string;
  sauceBoss?: boolean | null;
}

interface UserSidebarProps {
  customer: Customer;
  vendorResults: any[];
  /**
   * Whether the account is queued for deletion. Renders the red
   * "Queued for Deletion" banner at the top of the sidebar.
   *
   * IMPORTANT: This flag lives at the CUSTOMER GET RESPONSE ROOT
   * (sibling of `customer` and `vendorResultsList`) — NOT on the
   * `customer` object. Callers must read it from the top-level
   * response (e.g. `result?.flaggedForDeletion`), never from
   * `customer.flaggedForDeletion` (that path is always undefined).
   *
   * Only strict boolean `true` renders the banner. `false`, `null`,
   * and `undefined` all hide it, so callers can pass the raw API
   * value without coercion but should prefer an explicit
   * `=== true` check at the callsite.
   *
   * @example
   * <UserSidebar
   *   customer={result.customer}
   *   vendorResults={result.vendorResultsList}
   *   flaggedForDeletion={result?.flaggedForDeletion === true}
   * />
   */
  flaggedForDeletion?: boolean | null;
}

export const UserSidebar = ({
  customer,
  vendorResults,
  flaggedForDeletion,
}: UserSidebarProps) => {
  const isQueuedForDeletion = flaggedForDeletion === true;
  const isSauceBoss = customer.sauceBoss === true;

  return (
    <Card sx={{ width: 320, height: "fit-content", overflow: "hidden" }}>
      {isQueuedForDeletion && (
        <Tooltip
          title="This account is flagged for deletion. Contact privacy before taking action."
          arrow
          placement="top"
        >
          <Box
            role="status"
            aria-label="Account queued for deletion"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              bgcolor: "#d32f2f",
              color: "#fff",
              py: 1,
              px: 1.5,
              animation: "queuedForDeletionPulse 2.4s ease-in-out infinite",
              "@keyframes queuedForDeletionPulse": {
                "0%, 100%": { boxShadow: "inset 0 0 0 0 rgba(255,255,255,0)" },
                "50%": { boxShadow: "inset 0 -3px 0 0 rgba(255,255,255,0.35)" },
              },
            }}
          >
            <ShieldIcon sx={{ fontSize: 20 }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              Queued for Deletion
            </Typography>
          </Box>
        </Tooltip>
      )}

      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <Avatar sx={{ width: 50, height: 50, bgcolor: "#666" }}>
              {customer.firstName?.charAt(0) ?? ""}{" "}
              {customer.lastName?.charAt(0) ?? ""}
            </Avatar>
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                noWrap
              >
                {customer.firstName ?? ""} {customer.lastName ?? ""}
              </Typography>

              {isSauceBoss && (
                <Tooltip
                  title="Sauce Boss loyalty member"
                  arrow
                  placement="right"
                >
                  <Chip
                    size="small"
                    icon={
                      <LocalFireDepartmentIcon
                        sx={{ color: "#fff !important" }}
                      />
                    }
                    label="Sauce Boss VIP"
                    sx={{
                      mt: 0.5,
                      alignSelf: "flex-start",
                      color: "#fff",
                      fontWeight: 700,
                      letterSpacing: "0.02em",
                      background:
                        "linear-gradient(135deg, #F4A100 0%, #D64100 100%)",
                      boxShadow: "0 1px 4px rgba(214, 65, 0, 0.35)",
                      "& .MuiChip-icon": { ml: 0.75 },
                    }}
                  />
                </Tooltip>
              )}

              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mt: 0.5 }}
              >
                <Checkbox
                  checked={customer.privacySettings?.gpcSignal}
                  disabled
                  size="small"
                  sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 16 } }}
                />
                <Typography variant="caption">
                  Opted Out of Data Sharing
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Divider />

          <VendorStatus
            vendorResults={vendorResults}
            customer={customer}
          />

          <Divider />

          <ContactInfo customer={customer} />

          <Divider />

          <AdditionalInfo customer={customer} />
        </Stack>
      </CardContent>
    </Card>
  );
};
